import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { 
  Flame, 
  Scale, 
  Zap, 
  Shirt, 
  Scissors, 
  Tag, 
  Factory, 
  User, 
  Package, 
  DollarSign, 
  Search,
  Palette,
  Ruler,
  Star,
  BookOpen,
  type LucideIcon
} from 'lucide-react';
import type { Facet, ProductItem } from './types';

type SortMode = 'default' | 'collection' | 'price_low' | 'price_high' | 'weight_high' | 'weight_low';

interface ProductFilterToolbarProps {
  facets: Facet[];
  products: ProductItem[];
  onFilterChange: (filters: Record<string, string | null>) => void;
  onSortChange: (sortMode: SortMode) => void;
  activeFilters: Record<string, string | null>;
  currentSort: SortMode;
  totalCount: number;
  filteredCount: number;
}

/**
 * ProductFilterToolbar - Modern filter and sort bar for product displays
 * 
 * Features:
 * - Compact horizontal layout with facet pills
 * - Sort by collection, price, or default
 * - Expandable dropdowns for facets with many values
 * - Special styling for safety-related facets (FR rating, Hi-Vis)
 * - Collection count indicator when collections are present
 */
const ProductFilterToolbar: React.FC<ProductFilterToolbarProps> = ({
  facets,
  products,
  onFilterChange,
  onSortChange,
  activeFilters,
  currentSort,
  totalCount,
  filteredCount
}) => {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [showAllFacets, setShowAllFacets] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Count collections in products
  const collectionInfo = useMemo(() => {
    const collectionIds = new Set<string>();
    products.forEach(p => {
      if (p.is_collection && p.collection_id) {
        collectionIds.add(p.collection_id);
      }
    });
    return {
      count: collectionIds.size,
      hasCollections: collectionIds.size > 0
    };
  }, [products]);

  // Get facet icon based on field name
  const getFacetIcon = useCallback((fieldName: string): LucideIcon => {
    const icons: Record<string, LucideIcon> = {
      'fr_rating': Flame,
      'fire_resistant': Flame,
      'weight_oz': Scale,
      'hi_vis': Zap,
      'hi_visibility': Zap,
      'sleeve_length': Shirt,
      'fabrics': Scissors,
      'parent_brand': Tag,
      'vendorname': Factory,
      'gender': User,
      'product_sub_type_category': Package,
      'std_cost': DollarSign,
      'color_keywords': Palette,
      'color': Palette,
      'color_variant': Palette,
      'sizes': Ruler,
      'size': Ruler,
      'made_by_unifirst': Star,
      'is_catalog_item': BookOpen,
      'price_bucket': DollarSign,
    };
    return icons[fieldName] || Search;
  }, []);

  // Render facet icon component
  const renderFacetIcon = useCallback((fieldName: string, className: string = "w-3.5 h-3.5") => {
    const IconComponent = getFacetIcon(fieldName);
    return <IconComponent className={className} />;
  }, [getFacetIcon]);

  // Map color names to Tailwind classes for color facet swatches
  const getColorSwatchClass = useCallback((colorName: string): string => {
    const colorLower = colorName.toLowerCase();
    const colorMap: Record<string, string> = {
      'red': 'bg-red-500',
      'crimson': 'bg-red-700',
      'blue': 'bg-blue-500',
      'navy': 'bg-blue-900',
      'royal': 'bg-blue-700',
      'light blue': 'bg-blue-300',
      'green': 'bg-green-500',
      'forest': 'bg-green-800',
      'teal': 'bg-teal-500',
      'olive': 'bg-olive-600',
      'yellow': 'bg-yellow-400',
      'golden': 'bg-yellow-600',
      'orange': 'bg-orange-500',
      'purple': 'bg-purple-500',
      'burgundy': 'bg-rose-800',
      'pink': 'bg-pink-400',
      'brown': 'bg-amber-800',
      'tan': 'bg-amber-300',
      'khaki': 'bg-amber-200',
      'gray': 'bg-gray-500',
      'grey': 'bg-gray-500',
      'charcoal': 'bg-gray-700',
      'black': 'bg-gray-900',
      'white': 'bg-white border border-gray-300',
      'cream': 'bg-amber-50',
      'silver': 'bg-gray-300',
    };
    
    for (const [key, className] of Object.entries(colorMap)) {
      if (colorLower.includes(key)) return className;
    }
    return 'bg-gray-400';
  }, []);

  // Get style class for safety-related values
  const getSafetyStyle = useCallback((facet: Facet, value: string, isActive: boolean): string => {
    // FR rating styles
    if (facet.field_name === 'fr_rating' || facet.display_name.toLowerCase().includes('fr')) {
      if (value.includes('CAT 4') || value.includes('CAT 3')) {
        return isActive 
          ? 'bg-red-600 text-white border-red-700 shadow-red-200' 
          : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100 hover:border-red-300';
      }
      if (value.includes('CAT 2')) {
        return isActive
          ? 'bg-orange-500 text-white border-orange-600 shadow-orange-200'
          : 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100 hover:border-orange-300';
      }
      if (value.includes('CAT 1')) {
        return isActive
          ? 'bg-amber-500 text-white border-amber-600 shadow-amber-200'
          : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 hover:border-amber-300';
      }
    }
    
    // Hi-vis styles
    if (facet.field_name === 'hi_vis' || facet.field_name === 'hi_visibility' || 
        facet.display_name.toLowerCase().includes('vis')) {
      return isActive
        ? 'bg-lime-500 text-white border-lime-600 shadow-lime-200'
        : 'bg-lime-50 text-lime-700 border-lime-200 hover:bg-lime-100 hover:border-lime-300';
    }
    
    // Size facet styles - use ruler icon styling
    if (facet.field_name === 'sizes' || facet.field_name === 'size') {
      return isActive
        ? 'bg-indigo-600 text-white border-indigo-700 shadow-indigo-200'
        : 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100 hover:border-indigo-300';
    }
    
    // Color facet - default styling (swatch added separately)
    if (facet.field_name === 'color_keywords' || facet.field_name === 'color' || facet.field_name === 'color_variant') {
      return isActive
        ? 'bg-violet-600 text-white border-violet-700 shadow-violet-200'
        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300';
    }

    // Made by UniFirst — emerald accent (the "UniFirst made" pill on cards
    // uses the same family) so the provenance is visually consistent.
    if (facet.field_name === 'made_by_unifirst') {
      if (value === 'UniFirst Made') {
        return isActive
          ? 'bg-emerald-600 text-white border-emerald-700 shadow-emerald-200'
          : 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100 hover:border-emerald-400';
      }
      return isActive
        ? 'bg-slate-600 text-white border-slate-700 shadow-slate-200'
        : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100 hover:border-slate-300';
    }

    // Catalogue (DPC presence) — purple accent to distinguish from UniFirst.
    if (facet.field_name === 'is_catalog_item') {
      if (value === 'In Catalogue') {
        return isActive
          ? 'bg-purple-600 text-white border-purple-700 shadow-purple-200'
          : 'bg-purple-50 text-purple-700 border-purple-300 hover:bg-purple-100 hover:border-purple-400';
      }
      return isActive
        ? 'bg-slate-600 text-white border-slate-700 shadow-slate-200'
        : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100 hover:border-slate-300';
    }

    // Default UniFirst green theme
    return isActive
      ? 'bg-green-700 text-white border-green-800 shadow-green-200'
      : 'bg-white text-gray-700 border-gray-200 hover:bg-green-50 hover:border-green-300';
  }, []);

  // Handle facet value selection
  const handleValueSelect = useCallback((facet: Facet, value: string | null) => {
    const newFilters = { ...activeFilters };
    if (value === null || newFilters[facet.field_name] === value) {
      // Toggle off if clicking same value
      newFilters[facet.field_name] = null;
    } else {
      newFilters[facet.field_name] = value;
    }
    onFilterChange(newFilters);
    setOpenDropdown(null);
  }, [activeFilters, onFilterChange]);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    onFilterChange({});
    onSortChange('default');
  }, [onFilterChange, onSortChange]);

  // Count active filters
  const activeFilterCount = useMemo(() => 
    Object.values(activeFilters).filter(v => v !== null).length,
    [activeFilters]
  );

  // Prioritize facets: provenance/sales-channel toggles first (highest signal
  // for catalog work), then key safety facets, then commodity categoricals.
  const sortedFacets = useMemo(() => {
    const priorityOrder = [
      'made_by_unifirst',
      'is_catalog_item',
      'product_sub_type_category',
      'fr_rating',
      'hi_vis',
      'weight_oz',
      'fabrics',
      'parent_brand',
      'color_variant',
      'price_bucket',
    ];
    return [...facets].sort((a, b) => {
      const aIdx = priorityOrder.indexOf(a.field_name);
      const bIdx = priorityOrder.indexOf(b.field_name);
      if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
      if (aIdx !== -1) return -1;
      if (bIdx !== -1) return 1;
      return 0;
    });
  }, [facets]);

  // Determine which facets to show inline vs dropdown
  const { inlineFacets, dropdownFacets } = useMemo(() => {
    const inline: Facet[] = [];
    const dropdown: Facet[] = [];
    
    sortedFacets.forEach(facet => {
      // Show inline if 3 or fewer values, dropdown otherwise
      if (facet.values.length <= 3) {
        inline.push(facet);
      } else {
        dropdown.push(facet);
      }
    });
    
    return { inlineFacets: inline, dropdownFacets: dropdown };
  }, [sortedFacets]);

  // Get facets to display (limited or all). 8 inline keeps the high-signal
  // provenance toggles (UniFirst, Catalogue) visible without expanding.
  const visibleFacets = showAllFacets ? sortedFacets : sortedFacets.slice(0, 8);

  if (!facets || facets.length === 0) {
    // Still show sort options even without facets
    if (!collectionInfo.hasCollections) {
      return null;
    }
  }

  return (
    <div className="product-filter-toolbar mb-4 bg-gradient-to-r from-green-100 to-green-50 rounded-xl border border-green-300 shadow-sm">
      {/* Main toolbar row */}
      <div className="px-4 py-3">
        {/* Header with counts and actions */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Refine Results
            </span>
            {filteredCount !== totalCount && (
              <span className="text-xs px-2 py-0.5 bg-green-100 text-green-800 rounded-full font-medium">
                {filteredCount} of {totalCount}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {/* Standard Cost sort buttons */}
            <div className="flex items-center gap-1 border-r border-gray-200 pr-2">
              <DollarSign className="w-3.5 h-3.5 text-gray-400 mr-1" />
              <span className="text-[10px] text-gray-400 mr-1">Std Cost</span>
              <button
                onClick={() => onSortChange(currentSort === 'price_low' ? 'default' : 'price_low')}
                className={`
                  px-2 py-1 text-xs font-medium rounded-l-lg border-l border-t border-b
                  transition-all duration-200
                  ${currentSort === 'price_low'
                    ? 'bg-green-700 text-white border-green-800'
                    : 'bg-white text-gray-500 border-gray-200 hover:border-green-400 hover:text-green-700'
                  }
                `}
                title="Sort by standard cost: Low → High"
              >
                ↑ Low
              </button>
              <button
                onClick={() => onSortChange(currentSort === 'price_high' ? 'default' : 'price_high')}
                className={`
                  px-2 py-1 text-xs font-medium rounded-r-lg border
                  transition-all duration-200
                  ${currentSort === 'price_high'
                    ? 'bg-green-700 text-white border-green-800'
                    : 'bg-white text-gray-500 border-gray-200 hover:border-green-400 hover:text-green-700'
                  }
                `}
                title="Sort by standard cost: High → Low"
              >
                ↓ High
              </button>
            </div>

            {/* Weight sort buttons - only show if weight facet exists */}
            {facets.some(f => f.field_name === 'weight_oz') && (
              <div className="flex items-center gap-1 border-r border-gray-200 pr-2">
                <Scale className="w-3.5 h-3.5 text-gray-400 mr-1" />
                <button
                  onClick={() => onSortChange(currentSort === 'weight_high' ? 'default' : 'weight_high')}
                  className={`
                    px-2 py-1 text-xs font-medium rounded-l-lg border-l border-t border-b
                    transition-all duration-200
                    ${currentSort === 'weight_high'
                      ? 'bg-amber-600 text-white border-amber-700'
                      : 'bg-white text-gray-500 border-gray-200 hover:border-amber-300 hover:text-amber-600'
                    }
                  `}
                  title="Sort by weight: Heavy → Light"
                >
                  ↓ Heavy
                </button>
                <button
                  onClick={() => onSortChange(currentSort === 'weight_low' ? 'default' : 'weight_low')}
                  className={`
                    px-2 py-1 text-xs font-medium rounded-r-lg border
                    transition-all duration-200
                    ${currentSort === 'weight_low'
                      ? 'bg-amber-600 text-white border-amber-700'
                      : 'bg-white text-gray-500 border-gray-200 hover:border-amber-300 hover:text-amber-600'
                    }
                  `}
                  title="Sort by weight: Light → Heavy"
                >
                  ↑ Light
                </button>
              </div>
            )}

            {/* Collection sort toggle */}
            {collectionInfo.hasCollections && (
              <button
                onClick={() => onSortChange(currentSort === 'collection' ? 'default' : 'collection')}
                className={`
                  flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
                  border transition-all duration-200
                  ${currentSort === 'collection'
                    ? 'bg-indigo-600 text-white border-indigo-700 shadow-sm'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300 hover:text-indigo-600'
                  }
                `}
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
                <span>Collections</span>
                <span className={`
                  text-[10px] px-1.5 py-0.5 rounded-full ml-0.5
                  ${currentSort === 'collection' ? 'bg-white/20' : 'bg-gray-100'}
                `}>
                  {collectionInfo.count}
                </span>
              </button>
            )}
            
            {/* Clear all */}
            {(activeFilterCount > 0 || currentSort !== 'default') && (
              <button
                onClick={clearAllFilters}
                className="flex items-center gap-1 px-2 py-1.5 text-xs text-gray-500 hover:text-red-600 transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear all
              </button>
            )}
          </div>
        </div>

        {/* Facet filters row */}
        {facets && facets.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            {/* Inline pill facets (few values) */}
            {inlineFacets.slice(0, showAllFacets ? inlineFacets.length : 2).map(facet => (
              <div key={facet.id} className="flex items-center gap-1">
                <span className="text-gray-500 mr-0.5">{renderFacetIcon(facet.field_name, "w-3.5 h-3.5")}</span>
                {facet.values.map(value => {
                  const isActive = activeFilters[facet.field_name] === value;
                  const count = facet.style_codes_by_value[value]?.length || 0;
                  const isColorFacet = facet.field_name === 'color_keywords' || facet.field_name === 'color';
                  
                  return (
                    <button
                      key={value}
                      onClick={() => handleValueSelect(facet, value)}
                      className={`
                        inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full
                        border transition-all duration-200
                        ${getSafetyStyle(facet, value, isActive)}
                        ${isActive ? 'shadow-sm' : ''}
                      `}
                    >
                      {/* Color swatch for color facets */}
                      {isColorFacet && (
                        <span 
                          className={`w-3 h-3 rounded-full ${getColorSwatchClass(value)} shrink-0`}
                          aria-hidden="true"
                        />
                      )}
                      <span>{value}</span>
                      <span className="opacity-60 text-[10px]">{count}</span>
                    </button>
                  );
                })}
              </div>
            ))}

            {/* Dropdown facets (many values) */}
            {dropdownFacets.slice(0, showAllFacets ? dropdownFacets.length : 3).map(facet => {
              const isOpen = openDropdown === facet.id;
              const activeValue = activeFilters[facet.field_name];
              
              return (
                <div key={facet.id} className="relative" ref={isOpen ? dropdownRef : null}>
                  <button
                    onClick={() => setOpenDropdown(isOpen ? null : facet.id)}
                    className={`
                      flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
                      border transition-all duration-200
                      ${activeValue
                        ? 'bg-green-50 text-green-800 border-green-300'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                      }
                    `}
                  >
                    {renderFacetIcon(facet.field_name, "w-3.5 h-3.5")}
                    <span className="max-w-[100px] truncate">
                      {activeValue || facet.display_name}
                    </span>
                    <svg 
                      className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                    {activeValue && (
                      <span
                        onClick={(e) => { e.stopPropagation(); handleValueSelect(facet, null); }}
                        className="ml-0.5 p-0.5 rounded-full hover:bg-red-100 text-gray-400 hover:text-red-500"
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </span>
                    )}
                  </button>
                  
                  {/* Dropdown menu */}
                  {isOpen && (
                    <div className="absolute z-30 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 max-h-60 overflow-y-auto animate-in fade-in slide-in-from-top-1 duration-150">
                      {/* Clear option if active */}
                      {activeValue && (
                        <button
                          onClick={() => handleValueSelect(facet, null)}
                          className="w-full px-3 py-2 text-left text-xs text-gray-500 hover:bg-gray-50 border-b border-gray-100 flex items-center gap-2"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          Clear selection
                        </button>
                      )}
                      
                      {facet.values.map(value => {
                        const count = facet.style_codes_by_value[value]?.length || 0;
                        const isValueActive = activeValue === value;
                        const isColorFacet = facet.field_name === 'color_keywords' || facet.field_name === 'color';
                        
                        return (
                          <button
                            key={value}
                            onClick={() => handleValueSelect(facet, value)}
                            className={`
                              w-full px-3 py-2 text-left text-xs flex items-center justify-between
                              transition-colors
                              ${isValueActive 
                                ? 'bg-green-50 text-green-800' 
                                : 'text-gray-700 hover:bg-gray-50'
                              }
                            `}
                          >
                            <span className="flex items-center gap-2">
                              {/* Color swatch for color facets */}
                              {isColorFacet && (
                                <span 
                                  className={`w-3.5 h-3.5 rounded-full ${getColorSwatchClass(value)} shrink-0 border border-gray-200`}
                                  aria-hidden="true"
                                />
                              )}
                              {isValueActive && !isColorFacet && (
                                <svg className="w-3.5 h-3.5 text-green-700" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                              {isColorFacet && isValueActive && (
                                <svg className="w-3.5 h-3.5 text-green-700 -ml-5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                              <span className={isValueActive ? 'font-medium' : ''}>{value}</span>
                            </span>
                            <span className={`text-[10px] ${isValueActive ? 'text-green-600' : 'text-gray-400'}`}>
                              {count}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Show more/less facets toggle */}
            {sortedFacets.length > 5 && (
              <button
                onClick={() => setShowAllFacets(!showAllFacets)}
                className="text-xs text-green-700 hover:text-green-800 font-medium flex items-center gap-1"
              >
                {showAllFacets ? (
                  <>
                    <span>Show less</span>
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  </>
                ) : (
                  <>
                    <span>+{sortedFacets.length - 5} more</span>
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slide-in-from-top-1 {
          from { transform: translateY(-4px); }
          to { transform: translateY(0); }
        }
        .animate-in {
          animation: fade-in 0.15s ease-out, slide-in-from-top-1 0.15s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ProductFilterToolbar;


